import {
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  Grid,
  Paper,
  Fade,
  LinearProgress,
  IconButton,
  alpha,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useHistory, useParams } from 'react-router-dom';

import { UploadStreamResponse } from '@/types/document';
import { VaultDocument } from '@/types/vault';

import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import { useAssignDocumentToFolder } from '@/api/endpoints/document/useAssignDocumentToFolder';
import { useDeleteVaultDocument } from '@/api/endpoints/vault/useDeleteVaultDocument';
import { useGetDocumentCategories } from '@/api/endpoints/vault/useGetDocumentCategories';
import { useGetStagedFiles } from '@/api/endpoints/vault/useGetStagedFiles';
import { useGetVaultDocuments } from '@/api/endpoints/vault/useGetVaultDocuments';
import { CreditCard01Icon } from '@/assets/icons/CreditCard01Icon';
import { File06Icon } from '@/assets/icons/File06Icon';
import { FileCheck02Icon } from '@/assets/icons/FileCheck02Icon';
import { FolderIcon } from '@/assets/icons/FolderIcon';
import { Lightbulb01Icon } from '@/assets/icons/Lightbulb01Icon';
import { Mail01Icon } from '@/assets/icons/Mail01Icon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { UploadCloud01Icon } from '@/assets/icons/UploadCloud01Icon';
import { XIcon } from '@/assets/icons/XIcon';
import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { useDocumentUploadV2 } from '@/hooks/useDocumentUploadV2';
import { getResolvedRoutePath } from '@/utils/router';

import DocumentUploadButton, {
  API as DocumentUploadButtonAPI,
} from '@/components/document/DocumentUploadButton';
import DocumentViewModal from '@/components/document/DocumentViewModal';
import StagedFilesHierarchicalView from '@/components/document/StagedFilesHierarchicalView';
import FolderView from '@/components/document-folder/FolderView';
import NoPropertyLocation from '@/components/property-manager-portal/NoPropertyLocation';
import VaultDocumentTagModal from '@/components/vault/VaultDocumentTagModal';

// Helper function to get status message

interface FilterState {
  category: string;
  tags: string[];
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
}

interface VaultPageParams {
  folderId?: string;
}

const VaultPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { session } = useAuth();
  const { selectedPropertyLocation, propertyLocations } = usePropertyManager();
  const location = useLocation();
  const history = useHistory();
  const { folderId } = useParams<VaultPageParams>();
  const pendingCategoryIdRef = useRef<number | null>(null);
  const dialogOpenedRef = useRef<boolean>(false);
  const documentUploadButtonRef = useRef<DocumentUploadButtonAPI>(null);
  const queryClient = useQueryClient();

  // --- State Hooks ---
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<VaultDocument | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    category: '',
    tags: [],
    dateRange: {
      start: null,
      end: null,
    },
  });
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
  const [previewDocumentCategoryId] = useState<number | null>(null);
  const [, setPendingUploads] = useState<Set<string>>(new Set());
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [showEmptyState, setShowEmptyState] = useState(false);

  const [currentFolderId, setCurrentFolderId] = useState<number | null>(
    folderId ? Number(folderId) : null
  );

  // --- API Hooks ---
  const {
    data: vaultDocuments,
    refetch,
    isLoading,
  } = useGetVaultDocuments(
    {
      property_location_id: selectedPropertyLocation?.id,
    },
    !!session?.access_token && !!selectedPropertyLocation?.id
  );

  const { data: categories = [] } = useGetDocumentCategories();

  // Staged files hooks
  const { data: stagedFiles = [] } = useGetStagedFiles(!!session?.access_token);

  const deleteDocumentMutation = useDeleteVaultDocument({
    onSuccess: () => {
      refetch();
    },
  });

  const assignDocumentToFolderMutation = useAssignDocumentToFolder();

  // Single useDocumentUploadV2 instance for streaming upload and processing
  const { uploadFile: uploadWithStreaming, isUploading } = useDocumentUploadV2({
    onProgress: (response: UploadStreamResponse) => {
      // If status is completed but no document_id, trigger a refetch to get the document
      if (response.status === 'completed' && !response.document_id) {
        setPendingUploads(new Set());
        refetch();
        // Invalidate folder queries to refresh the folder list
        queryClient.invalidateQueries({ queryKey: ['folders'] });
        return;
      }

      // Handle progress updates
      if (response.document_id) {
        // Clear any pending uploads since we now have a document ID
        setPendingUploads(new Set());
        // Refetch to get the new document
        refetch();
        // Invalidate folder queries to refresh the folder list
        queryClient.invalidateQueries({ queryKey: ['folders'] });
      } else {
        // Update pending uploads with progress
      }
    },
    onComplete: (response: UploadStreamResponse) => {
      if (response.document_id) {
        // Trigger folder assignment after successful upload
        assignDocumentToFolderMutation.mutate(response.document_id);

        refetch();
        // Invalidate folder queries to refresh the folder list
        queryClient.invalidateQueries({ queryKey: ['folders'] });
      } else {
        // No document_id in completion, clean up pending uploads and refetch
        setPendingUploads(new Set());
        setTimeout(() => {
          refetch();
          // Invalidate folder queries to refresh the folder list
          queryClient.invalidateQueries({ queryKey: ['folders'] });
        }, 1000);
      }
    },
    onError: (errorMessage: string) => {
      // Improved error handling for timeouts
      if (errorMessage.includes('Upload timed out') || errorMessage.includes('502')) {
        enqueueSnackbar(
          'Upload is taking longer than expected. The file has been uploaded and is being processed in the background. Please refresh the page in a few minutes to see the document.',
          {
            variant: 'warning',
            persist: true,
            action: (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  refetch();
                }}
              >
                Refresh Now
              </Button>
            ),
          }
        );
      } else {
        enqueueSnackbar(`Upload failed: ${errorMessage}`, { variant: 'error' });
      }
      // Clean up any pending uploads
      setPendingUploads(new Set());
    },
  });

  const handleDeleteConfirm = () => {
    if (documentToDelete) {
      deleteDocumentMutation.mutate({ documentId: documentToDelete });
    }
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  const handleTagModalClose = () => {
    setIsTagModalOpen(false);
    setSelectedDocument(null);
  };

  const handleTagModalSuccess = () => {
    setIsTagModalOpen(false);
    setSelectedDocument(null);
    refetch();
  };

  const handleFilterClose = () => {
    setFilterDialogOpen(false);
  };

  const handleFilterApply = (newFilters: FilterState) => {
    setFilters(newFilters);
    setFilterDialogOpen(false);
  };

  const handleFilterReset = () => {
    setFilters({
      category: '',
      tags: [],
      dateRange: {
        start: null,
        end: null,
      },
    });
  };

  const handleUploadClick = useCallback(() => {
    documentUploadButtonRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!selectedPropertyLocation?.id) {
        enqueueSnackbar('Please select a property location first', { variant: 'error' });
        return;
      }

      // Create a temporary identifier for this upload
      const uploadId = `upload_${Date.now()}_${file.name}`;

      // Create temporary upload entry to show in the list immediately
      setPendingUploads(new Set([uploadId]));

      uploadWithStreaming({
        file,
        location_ids: [selectedPropertyLocation.id],
        folder_id: currentFolderId,
        tags: {
          property_location_ids: [selectedPropertyLocation.id],
          building_ids: [],
          unit_ids: [],
          tenant_ids: [],
          property_manager_ids: [],
        },
      });
    },
    [selectedPropertyLocation?.id, uploadWithStreaming, currentFolderId, setPendingUploads]
  );

  // Handle sample file creation
  const handleSampleFileUpload = useCallback(
    (type: 'lease' | 'utility' | 'receipt') => {
      if (!selectedPropertyLocation?.id) {
        enqueueSnackbar('Please select a property location first', { variant: 'error' });
        return;
      }

      // Create sample file based on type
      let content = '';
      let fileName = '';

      switch (type) {
      case 'lease':
        content = `RESIDENTIAL LEASE AGREEMENT
          
Property Address: 123 Main Street, Apt 4B, New York, NY 10001
Landlord: Property Management LLC
Tenant: John Doe
Lease Term: 12 months
Start Date: January 1, 2024
End Date: December 31, 2024
Monthly Rent: $2,500
Security Deposit: $2,500
Due Date: 1st of each month

TERMS AND CONDITIONS...`;
        fileName = 'sample-lease-agreement.pdf';
        break;
      case 'utility':
        content = `UTILITY BILL
          
Account Number: 123456789
Service Address: 123 Main Street, Apt 4B
Billing Period: December 1-31, 2023
Due Date: January 15, 2024

Electricity Usage: 450 kWh
Amount Due: $125.50

PLEASE PAY BY DUE DATE...`;
        fileName = 'sample-utility-bill.pdf';
        break;
      case 'receipt':
        content = `RECEIPT
          
Date: December 15, 2023
Vendor: ABC Home Repairs
Service: Plumbing repair - kitchen sink

Labor: $150.00
Parts: $75.00
Total: $225.00

Payment Method: Credit Card
Thank you for your business!`;
        fileName = 'sample-receipt.pdf';
        break;
      }

      // Create a basic PDF structure (this is a minimal PDF that most viewers can open)
      const pdfContent = `%PDF-1.4
1 0 obj
<< /Type /Catalog /Pages 2 0 R >>
endobj
2 0 obj
<< /Type /Pages /Kids [3 0 R] /Count 1 >>
endobj
3 0 obj
<< /Type /Page /Parent 2 0 R /Resources << /Font << /F1 4 0 R >> >> /MediaBox [0 0 612 792] /Contents 5 0 R >>
endobj
4 0 obj
<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>
endobj
5 0 obj
<< /Length ${content.length + 100} >>
stream
BT
/F1 12 Tf
50 750 Td
(${content.replace(/\n/g, ') Tj T* (')}
) Tj
ET
endstream
endobj
xref
0 6
0000000000 65535 f
0000000009 00000 n
0000000058 00000 n
0000000115 00000 n
0000000262 00000 n
0000000341 00000 n
trailer
<< /Size 6 /Root 1 0 R >>
startxref
${content.length + 500}
%%EOF`;

      const blob = new Blob([pdfContent], { type: 'application/pdf' });
      const file = new File([blob], fileName, { type: 'application/pdf' });

      handleFileSelect(file);
      enqueueSnackbar(`Sample ${type} document uploaded!`, { variant: 'success' });
    },
    [selectedPropertyLocation?.id, handleFileSelect]
  );

  // Check for openDocumentUpload query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openDocumentUpload') === 'true' && !dialogOpenedRef.current) {
      // Mark that we've opened the dialog for this session
      dialogOpenedRef.current = true;

      // Store defaultCategoryId if present
      const defaultCategoryId = searchParams.get('defaultCategoryId');
      if (defaultCategoryId) {
        pendingCategoryIdRef.current = Number(defaultCategoryId);
      }

      // Clear URL parameters immediately
      const newUrl = new URL(window.location.href);
      newUrl.search = '';
      history.replace(newUrl.pathname);

      // Open the upload dialog
      handleUploadClick();
    }
  }, [location.search, handleUploadClick, history]);

  // Refetch documents when selected property location changes
  useEffect(() => {
    if (selectedPropertyLocation?.id && session?.access_token) {
      refetch();
    }
  }, [selectedPropertyLocation?.id, session?.access_token, refetch]);

  // Track total documents
  useEffect(() => {
    if (vaultDocuments) {
      setTotalDocuments(vaultDocuments.length);
    }
  }, [vaultDocuments]);

  // Handle folder navigation
  const handleFolderChange = useCallback(
    (newFolderId: number | null) => {
      setCurrentFolderId(newFolderId);
      if (newFolderId) {
        history.push(
          getResolvedRoutePath(PMPRoutes.vaultFolder, {
            folderId: String(newFolderId),
          })
        );
      } else {
        history.push(PMPRoutes.vault);
      }
    },
    [history]
  );

  // Sync URL folder ID with state
  useEffect(() => {
    if (folderId) {
      setCurrentFolderId(Number(folderId));
    } else {
      setCurrentFolderId(null);
    }
  }, [folderId]);

  // Show NoPropertyLocation if no properties exist
  if (propertyLocations.length === 0) {
    return (
      <MuiPageWrapper>
        <Box>
          <Stack direction="column" spacing={3}>
            <DashboardHeader hasInnerHtml title="Document Vault" />
            <NoPropertyLocation
              title="Let’s add your first property location!"
              description="Add your first property to start managing your documents and files in the vault."
            />
          </Stack>
        </Box>
      </MuiPageWrapper>
    );
  }

  // Show message if no location is selected
  if (!selectedPropertyLocation?.id) {
    return (
      <MuiPageWrapper>
        <Box>
          <Stack direction="column" spacing={3}>
            <DashboardHeader hasInnerHtml title="Documents: <em>Vault</em>" />
            <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              Please select a property location to view documents.
            </Typography>
          </Stack>
        </Box>
      </MuiPageWrapper>
    );
  }

  return (
    <MuiPageWrapper>
      <Box>
        <Stack direction="column" spacing={3}>
          <Box
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 0 },
              display: 'flex',
              justifyContent: { sm: 'space-between' },
              width: '100%',
            }}
          >
            <DashboardHeader hasInnerHtml title="Document Vault" />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 2 }}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {totalDocuments > 0 && (
                <Button
                  variant="outlined"
                  size={isMobile ? 'medium' : 'large'}
                  startIcon={<Lightbulb01Icon />}
                  onClick={() => setShowEmptyState(!showEmptyState)}
                  fullWidth={isMobile}
                  sx={{
                    textTransform: 'none',
                    borderColor: 'grey.300',
                    color: 'text.secondary',
                    py: isMobile ? 1 : 1.5,
                    borderRadius: 1.5,
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    },
                  }}
                >
                  {showEmptyState ? 'Hide' : 'Show'} Upload Guide
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<PlusIcon />}
                onClick={handleUploadClick}
                disabled={isLoading || !selectedPropertyLocation?.id || isUploading}
                fullWidth={isMobile}
                size={isMobile ? 'medium' : 'large'}
                sx={{
                  py: isMobile ? 1 : 1.5,
                  borderRadius: 1.5,
                }}
              >
                Upload Document
              </Button>
            </Stack>
          </Box>

          {/* Benefits Bar */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              borderRadius: 2,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems="center"
              justifyContent="space-around"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Lightbulb01Icon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="body2" fontWeight={500}>
                  Instant AI categorization
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <FileCheck02Icon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="body2" fontWeight={500}>
                  Extract key dates & amounts
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                component="a"
                href={PMPRoutes.chatWithAI}
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                <SearchMdIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="body2" fontWeight={500}>
                  Search any detail in seconds
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          {/* Enhanced Empty State and Upload CTAs */}
          {(totalDocuments === 0 || showEmptyState) && !isLoading && (
            <Fade in timeout={500}>
              <Box>
                {/* Hero Message */}
                <Card
                  elevation={0}
                  sx={{
                    bgcolor: 'grey.50',
                    border: '2px dashed',
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    mb: 3,
                  }}
                >
                  <Typography variant="h4" fontWeight={600} gutterBottom>
                    Your AI-Powered Document Hub
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                    Upload any document and our AI instantly organizes, extracts key info, and makes
                    everything searchable. Also adds key events to the calendar automagically.
                  </Typography>

                  {/* Visual illustration */}
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ mb: 4 }}
                  >
                    <File06Icon sx={{ fontSize: 48, color: 'grey.400' }} />
                    <Typography variant="h5" color="grey.400">
                      →
                    </Typography>
                    <Lightbulb01Icon sx={{ fontSize: 48, color: 'primary.main' }} />
                    <Typography variant="h5" color="grey.400">
                      →
                    </Typography>
                    <FolderIcon sx={{ fontSize: 48, color: 'success.main' }} />
                  </Stack>

                  {/* Progress Indicator */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {totalDocuments > 0
                        ? `${totalDocuments} documents uploaded | Keep adding to expand your vault`
                        : '0 documents uploaded | Get started to unlock AI insights'}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={totalDocuments > 0 ? 100 : 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'grey.200',
                        mt: 1,
                      }}
                    />
                  </Box>
                </Card>

                {/* Multiple Upload CTAs */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 3,
                        height: '100%',
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: 'primary.main',
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: theme.shadows[4],
                        },
                      }}
                      onClick={handleUploadClick}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <UploadCloud01Icon sx={{ fontSize: 48, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            Upload Files
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Drag & drop or click to browse
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 3,
                        height: '100%',
                        border: '2px solid',
                        borderColor: 'secondary.main',
                        bgcolor: alpha(theme.palette.secondary.main, 0.05),
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Mail01Icon sx={{ fontSize: 48, color: 'secondary.main' }} />
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            Email to share@terr.ai
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Forward documents from anywhere
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>

                {/* Try It Out Section - Only show when no documents */}
                {totalDocuments === 0 && (
                  <Card elevation={0} sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Try It Out - Upload a Sample Document
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      See how our AI categorizes and extracts information from different document
                      types
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Button
                        variant="outlined"
                        startIcon={<File06Icon />}
                        onClick={() => handleSampleFileUpload('lease')}
                        sx={{ textTransform: 'none' }}
                      >
                        Try with a sample lease
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CreditCard01Icon />}
                        onClick={() => handleSampleFileUpload('utility')}
                        sx={{ textTransform: 'none' }}
                      >
                        Try with a utility bill
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<FileCheck02Icon />}
                        onClick={() => handleSampleFileUpload('receipt')}
                        sx={{ textTransform: 'none' }}
                      >
                        Try with a receipt
                      </Button>
                    </Stack>
                  </Card>
                )}

                {/* Onboarding Button */}
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<Lightbulb01Icon />}
                    onClick={() => setShowOnboarding(true)}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      px: 4,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Take a 30-second Tour
                  </Button>
                </Box>
              </Box>
            </Fade>
          )}

          <DocumentUploadButton
            ref={documentUploadButtonRef}
            isFileUploading={isUploading}
            onFileSelect={handleFileSelect}
            sx={{ display: 'none' }}
          />

          {/* Staged Files Section */}
          <StagedFilesHierarchicalView
            stagedFiles={stagedFiles}
            availableLocations={propertyLocations.map(loc => ({
              id: loc.id,
              name: loc.name,
              address_text: loc.address_text,
            }))}
            isLoading={false}
            onPreviewFile={(fileId: string) => {
              // Find the staged file and open its Google Drive link
              const stagedFile = stagedFiles.find(f => f.id === fileId);
              if (stagedFile?.metadata?.google_drive_web_view_link) {
                window.open(
                  stagedFile.metadata.google_drive_web_view_link,
                  '_blank',
                  'noopener,noreferrer'
                );
              }
            }}
            onFileAssigned={() => {
              // Comprehensive refresh after file assignment
              // Note: Most of this is redundant with useCompleteStagedFileUpload invalidation,
              // but we keep this as a backup to ensure real-time updates

              // 1. Refetch current vault documents
              refetch();

              // 2. Invalidate all relevant queries
              queryClient.invalidateQueries({
                queryKey: ['GET', 'document/google-drive/staged-files'],
              });
              queryClient.invalidateQueries({
                queryKey: ['GET', 'vault/documents'],
              });

              // 3. Invalidate folder queries for FolderView component
              queryClient.invalidateQueries({ queryKey: ['folders'] });
              queryClient.invalidateQueries({ queryKey: ['folders', 'hierarchy'] });
              queryClient.invalidateQueries({ queryKey: ['folders', 'contents'] });
              queryClient.invalidateQueries({ queryKey: ['folders', 'root-contents'] });
            }}
          />

          <FolderView
            currentFolderId={currentFolderId}
            propertyLocationId={selectedPropertyLocation?.id}
            onFolderChange={handleFolderChange}
          />

          {/*
          // --- Commented out: Old Table-based Document List ---
          <Card sx={{ bgcolor: 'common.white' }}>
            <DropZone onFileDrop={handleFileSelect} isUploading={isUploading}>
              ...
            </DropZone>
          </Card>
          */}
        </Stack>
      </Box>

      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        PaperProps={{
          sx: {
            minWidth: { xs: '90%', sm: 400 },
            maxWidth: { xs: '90%', sm: 500 },
          },
        }}
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this document?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {isTagModalOpen && selectedDocument && (
        <VaultDocumentTagModal
          document={selectedDocument}
          onClose={handleTagModalClose}
          onSuccess={handleTagModalSuccess}
        />
      )}

      <Dialog
        open={filterDialogOpen}
        onClose={handleFilterClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minWidth: { xs: '90%', sm: 400 },
            maxWidth: { xs: '90%', sm: 600 },
          },
        }}
      >
        <DialogTitle>Filter Documents</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Document Category
              </Typography>
              <Select
                fullWidth
                value={filters.category}
                onChange={e => setFilters(prev => ({ ...prev, category: e.target.value }))}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary[400],
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary[600],
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary[600],
                    borderWidth: '1px',
                  },
                }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.id.toString()}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Date Added
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={filters.dateRange.start?.toISOString().split('T')[0] || ''}
                  onChange={e =>
                    setFilters(prev => ({
                      ...prev,
                      dateRange: {
                        ...prev.dateRange,
                        start: e.target.value ? new Date(e.target.value) : null,
                      },
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[400],
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[600],
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[600],
                      borderWidth: '1px',
                    },
                  }}
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={filters.dateRange.end?.toISOString().split('T')[0] || ''}
                  onChange={e =>
                    setFilters(prev => ({
                      ...prev,
                      dateRange: {
                        ...prev.dateRange,
                        end: e.target.value ? new Date(e.target.value) : null,
                      },
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[400],
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[600],
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[600],
                      borderWidth: '1px',
                    },
                  }}
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFilterReset}>Reset</Button>
          <Button onClick={handleFilterClose}>Cancel</Button>
          <Button onClick={() => handleFilterApply(filters)} variant="contained">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {previewDocumentId && (
        <DocumentViewModal
          id={previewDocumentId}
          categoryId={previewDocumentCategoryId ?? undefined}
          onClose={() => setPreviewDocumentId(null)}
        />
      )}

      {/* Interactive Onboarding Dialog */}
      <Dialog
        open={showOnboarding}
        onClose={() => {
          setShowOnboarding(false);
          setOnboardingStep(0);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h5" fontWeight={600}>
            Welcome to Your Document Vault! 🎉
          </Typography>
          <IconButton
            onClick={() => {
              setShowOnboarding(false);
              setOnboardingStep(0);
            }}
          >
            <XIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            {onboardingStep === 0 && (
              <Fade in>
                <Box>
                  <Stack spacing={3}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <UploadCloud01Icon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Step 1: Upload Your Documents
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        You can upload documents in two easy ways:
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            Option 1: Direct Upload
                          </Typography>
                          <Typography variant="body2">
                            Click the "Upload Document" button or drag & drop files directly onto
                            the page
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            Option 2: Email
                          </Typography>
                          <Typography variant="body2">
                            Forward any document to <strong>share@terr.ai</strong> from your email
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Stack>
                </Box>
              </Fade>
            )}
            {onboardingStep === 1 && (
              <Fade in>
                <Box>
                  <Stack spacing={3}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Lightbulb01Icon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Step 2: AI Magic Happens
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Our AI automatically analyzes your documents:
                      </Typography>
                    </Box>
                    <Stack spacing={2}>
                      <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          ✓ Categorizes into folders
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Leases, bills, receipts - all organized automatically
                        </Typography>
                      </Paper>
                      <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          ✓ Extracts key information
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Dates, amounts, names - instantly searchable
                        </Typography>
                      </Paper>
                      <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          ✓ Makes everything searchable
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Find any detail in seconds with smart search
                        </Typography>
                      </Paper>
                    </Stack>
                  </Stack>
                </Box>
              </Fade>
            )}
            {onboardingStep === 2 && (
              <Fade in>
                <Box>
                  <Stack spacing={3}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <SearchMdIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Step 3: Search & Access Instantly
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Find any document or detail in seconds:
                      </Typography>
                    </Box>
                    <Stack spacing={2}>
                      <Typography variant="body1">
                        • Search by document name, content, or date
                      </Typography>
                      <Typography variant="body1">• Filter by categories and tags</Typography>
                      <Typography variant="body1">• Access from any device, anytime</Typography>
                    </Stack>
                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => {
                          setShowOnboarding(false);
                          setOnboardingStep(0);
                          handleSampleFileUpload('lease');
                        }}
                        sx={{ borderRadius: 2 }}
                      >
                        Try It Now with a Sample Document
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              </Fade>
            )}
          </Box>

          {/* Progress dots */}
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 3 }}>
            {[0, 1, 2].map(step => (
              <Box
                key={step}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: step === onboardingStep ? 'primary.main' : 'grey.300',
                  cursor: 'pointer',
                }}
                onClick={() => setOnboardingStep(step)}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          {onboardingStep > 0 && (
            <Button onClick={() => setOnboardingStep(onboardingStep - 1)}>Previous</Button>
          )}
          {onboardingStep < 2 ? (
            <Button variant="contained" onClick={() => setOnboardingStep(onboardingStep + 1)}>
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => {
                setShowOnboarding(false);
                setOnboardingStep(0);
              }}
            >
              Get Started
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Full page overlay loader during upload */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
        open={isUploading}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" color="inherit">
            Uploading and processing document...
          </Typography>
          <Typography variant="body2" color="inherit" sx={{ textAlign: 'center', maxWidth: 400 }}>
            Please wait while we upload your document and organize it into the appropriate folder.
          </Typography>
        </Stack>
      </Backdrop>
    </MuiPageWrapper>
  );
};

export default VaultPage;
